import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { NotificationService } from 'src/app/services/notification.service';
import { InboxService } from '../inbox.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { helper } from 'src/environments/helper';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { catchError, distinctUntilChanged, finalize, Observable, of, Subject, Subscription, switchMap, takeUntil, tap } from 'rxjs';
import { SabloaneComponent } from '../sabloane/sabloane.component';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { initializeEditor } from 'src/app/utils/editorConfig';
import { AppFilesService } from 'src/app/services/app-files.service';
import { RedirectionService } from 'src/app/platform/nomenclatoare/redirection-addresses/redirection.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { AddRedirectionComponent } from 'src/app/platform/nomenclatoare/redirection-addresses/add-redirection/add-redirection.component';
import { DialogService } from 'src/app/services/dialog.service';

@Component({
    selector: 'app-redirect-dialog',
    templateUrl: './redirect-dialog.component.html',
    styleUrls: ['./redirect-dialog.component.scss']
})
export class RedirectDialogComponent implements OnInit, OnDestroy {
    // general error
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;
    editorConfig!: AngularEditorConfig;

    emails!: any[];
    filteredEmails: any[] = [];
    emailControl = new FormControl('');
    emailSelectControl = new FormControl(null);

    selectedEmails: string[] = [];

    private destroy$ = new Subject<void>();
    private contentSubscription!: Subscription;
    private allEmails: any[] = []; // Păstrează toate emailurile originale

    form: FormGroup = new FormGroup({
        from_email: new FormControl(this.data.from_email),
        from_name: new FormControl(this.data.from_name),
        cc: new FormControl([this.data.to_cc, this.data.from_email].filter(email => email).join('; ')),
        subject: new FormControl(this.data.subject),
        body: new FormControl(this.data.body),
        email_id: new FormControl(this.data.id),
        attachments: new FormControl(this.data.attachments),
        redirect_emails_arr: new FormControl([], Validators.required),
        redirect_observatii: new FormControl(''),
    })

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private dialogRef: MatDialogRef<RedirectDialogComponent>,
        private inboxService: InboxService,
        private notificationService: NotificationService,
        private dialog: MatDialog,
        private appFilesService: AppFilesService,
        private redirectionAddressService: RedirectionService,
        public permissionsService: PermissionsService,
        private dialogService: DialogService
    ) { console.log(this.data); }

    ngOnInit(): void {
        this.editorConfig = initializeEditor();
        this.subscribeToContent();
        this.loadAddresses();
        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({
            success: false,
        });
    }

    renderFieldsSearch() {
        this.emailControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.updateFilteredEmails();
                    return of(this.filteredEmails);
                }
                return this.filterEmails(inputValue);
            }),
            tap((data: any[]) => {
                this.filteredEmails = data;
            }),
            catchError(() => of([]))
        ).subscribe();
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    loadAddresses() {
        this.isLoading = true;
        this.redirectionAddressService.list()
            .pipe(
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (results: any) => {
                    this.allEmails = results.data.filter((email: any) => email.active === 1);
                    this.emails = [...this.allEmails];
                    this.updateFilteredEmails();
                },
                error: (error) => {
                    console.error('Error in get adrese redirectionare: ', error);
                }
            })
    }

    updateFilteredEmails() {
        // Filtrează emailurile pentru a exclude cele deja selectate
        this.filteredEmails = this.emails.filter(email =>
            !this.selectedEmails.includes(email.email)
        );
    }

    filterEmails(searchTerm: string): Observable<any[]> {
        const normalizedSearchTerm = this.normalizeString(searchTerm);

        const filtered = this.emails.filter(email =>
            !this.selectedEmails.includes(email.email) &&
            this.normalizeString(email.email).includes(normalizedSearchTerm)
        );

        return of(filtered);
    }

    onEmailSelected(event: any) {
        const selectedEmail = event.value;

        if (selectedEmail && selectedEmail.email) {
            // Adaugă email.email în array
            this.selectedEmails.push(selectedEmail.email);

            // Actualizează form control cu array-ul de emails
            this.form.controls['redirect_emails_arr'].setValue([...this.selectedEmails]);

            // Resetează selectul și input-ul de search
            this.emailSelectControl.setValue(null);
            this.emailControl.setValue('');

            // Actualizează lista filtrată
            this.updateFilteredEmails();
        }
    }

    removeSelectedEmail(emailAddress: string) {
        // Elimină emailul din array
        this.selectedEmails = this.selectedEmails.filter(email => email !== emailAddress);

        // Actualizează form control
        this.form.controls['redirect_emails_arr'].setValue([...this.selectedEmails]);

        // Actualizează lista filtrată
        this.updateFilteredEmails();

        // Resetează input-ul de search pentru a reîmprospăta lista
        if (this.emailControl.value) {
            this.emailControl.setValue(this.emailControl.value);
        }
    }

    onTemplates() {
        const dialogRef = this.dialog.open(SabloaneComponent, {
            minHeight: '40vh',
            maxHeight: '100vh',
            minWidth: '50vw',
            maxWidth: '50vw',
            disableClose: true,
        });

        dialogRef
            .afterClosed()
            .subscribe(({ success }: { success: boolean }) => { });
    }

    subscribeToContent() {
        this.contentSubscription = this.inboxService.sablonContent$.subscribe({
            next: (content) => {
                this.form.controls['redirect_observatii'].setValue(content);
            },
        });
    }

    downloadAttachment(attachment: any) {
        let fullPath = this.inboxService.downloadUrl + attachment.id;
        this.isLoading = true;
        this.appFilesService.downloadFile(fullPath)
            .then(() => {
                this.isLoading = false;
            })
            .catch(err => {
                console.error('File download error: ', err);
                this.isLoading = false;
            })
    }

    openAddDestinationDialog() {
        const dialogRef = this.dialog.open(AddRedirectionComponent, {
            ...this.dialogService.getDialogConfig(),
        });
        dialogRef.afterClosed().subscribe((result: { success: boolean; data?: any }) => {
            if (result?.success && result?.data) {
                // Adaugă noul email în lista completă
                this.allEmails.push(result.data);
                this.emails = [...this.allEmails];

                // Adaugă automat emailul nou în lista de destinatari selectați
                this.selectedEmails.push(result.data.email);
                this.form.controls['redirect_emails_arr'].setValue([...this.selectedEmails]);

                // Actualizează lista filtrată
                this.updateFilteredEmails();

                // Opțional: Afișează o notificare de succes
                this.notificationService.successSwal(
                    'Succes',
                    `Destinatarul ${result.data.email} a fost adăugat cu succes!`,
                );
            }
        });

    }

    submitForm() {
        this.isLoading = true;

        this.inboxService
            .redirect(this.form.value)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: () => {
                    this.dialogRef.close({ success: true })
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        this.errorIcon
                    );
                }
            })
    }
}